import React, { useState, useEffect } from "react";
import type { ProjectSummary, ProjectCreatedData, SheetSummary, ComponentStatus } from "../types";
import ComponentStatusTable from "./ComponentStatusTable";
import PCBConfigModal from "./PCBConfigModal";
import type { PCBConfig } from "./PCBConfigModal";

export interface ProjectDashboardProps {
  userEmail: string;
  projects: ProjectSummary[];
  currentProject: ProjectCreatedData | null;
  onCreateProject: (description: string, enrichWithGemini?: boolean) => void;
  onAbortProject: () => void;
  onListProjects: () => void;
  onSelectProject: (projectId: string) => void;
  onBuildProject: (projectId: string) => void;
  onBuildPCB: (projectId: string, config?: PCBConfig) => void;
  onConfigurePCB: (projectId: string, config: PCBConfig) => void;
  onUploadSchematics: (projectId: string, files: Record<string, string>) => void;
  onFileSelected: (projectId: string, partNumber: string, file: File) => void;
  onDownloadComponentJson: (projectId: string, partNumber: string) => void;
  onGenerateAll: (projectId: string) => void;
  onForceUploadToggle: (partNumber: string, forced: boolean) => void;
  onUploadReference?: (projectId: string, file: File, userPrompt: string) => void;
  isCreating: boolean;
  isBuilding: boolean;
  isPCBRunning: boolean;
  pcbArchiveUrl: string | null;
  schematicsArchiveUrl: string | null;
  schematicReady: boolean;
  componentStatus: ComponentStatus[];
  datasheetUploads: Record<string, any>;
  forcedParts: Set<string>;
  referenceStatus?: string;
  enrichedPreview?: string;
  symbolInventory?: any;
  onSymbolApprove?: (projectId: string, partNumber: string, artifactHash: string) => void;
  onSymbolReject?: (projectId: string, partNumber: string, reason: string) => void;
  onSymbolDownload?: (projectId: string, partNumber: string) => void;
}

const ARCHIVE_BASE = "https://dndeexgjhs4b7.cloudfront.net/archives";

const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  userEmail,
  projects,
  currentProject,
  onCreateProject,
  onAbortProject,
  onListProjects,
  onSelectProject,
  onBuildProject,
  onBuildPCB,
  onConfigurePCB,
  onUploadSchematics,
  onFileSelected,
  onDownloadComponentJson,
  onGenerateAll,
  onForceUploadToggle,
  onUploadReference,
  isCreating,
  isBuilding,
  isPCBRunning,
  pcbArchiveUrl,
  schematicsArchiveUrl,
  schematicReady,
  componentStatus,
  datasheetUploads,
  forcedParts,
  referenceStatus,
  enrichedPreview,
  symbolInventory,
  onSymbolApprove,
  onSymbolReject,
  onSymbolDownload,
}) => {
  const [description, setDescription] = useState("");
  const [activeTab, setActiveTab] = useState<"current" | "history">("current");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showPCBConfig, setShowPCBConfig] = useState(false);
  const [uploadedSchematics, setUploadedSchematics] = useState<number | null>(null);
  const [pcbProfile, setPcbProfile] = useState<string>("generic_dynamic");
  const [pcbConfig, setPcbConfig] = useState<PCBConfig | null>(null);
  const [refFileName, setRefFileName] = useState<string>("");
  const [enrichWithGemini, setEnrichWithGemini] = useState<boolean>(false);

  const isProjectActive = isCreating || isBuilding || !!currentProject;

  const handleUploadSchematics = async (projectId: string, fileList: FileList) => {
    const file = fileList[0];
    if (!file) return;

    const files: Record<string, string> = {};

    if (file.name.endsWith('.zip')) {
      const { default: JSZip } = await import('jszip');
      const zip = await JSZip.loadAsync(file);
      for (const [path, entry] of Object.entries(zip.files)) {
        if (entry.dir || !path.endsWith('.kicad_sch')) continue;
        const content = await entry.async('uint8array');
        let binary = '';
        for (let j = 0; j < content.length; j++) {
          binary += String.fromCharCode(content[j]);
        }
        const filename = path.split('/').pop() || path;
        files[filename] = btoa(binary);
      }
    } else if (file.name.endsWith('.kicad_sch')) {
      const content = await file.arrayBuffer();
      const bytes = new Uint8Array(content);
      let binary = '';
      for (let j = 0; j < bytes.length; j++) {
        binary += String.fromCharCode(bytes[j]);
      }
      files[file.name] = btoa(binary);
    }

    if (Object.keys(files).length > 0) {
      onUploadSchematics(projectId, files);
      setUploadedSchematics(Object.keys(files).length);
    }
  };

  // Load project list on mount — defer until WS is likely connected
  useEffect(() => {
    const timer = setTimeout(() => {
      try { onListProjects(); } catch {}
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Auto-switch to current tab when a project is created
  useEffect(() => {
    if (currentProject) setActiveTab("current");
  }, [currentProject?.project_id]);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (description.trim()) {
      onCreateProject(description.trim(), enrichWithGemini);
      setDescription("");
    }
  };

  return (
    <div className="project-dashboard">
      {/* ── Tabs ── */}
      <div className="pd-tabs">
        <button
          className={`pd-tab ${activeTab === "current" ? "pd-tab-active" : ""}`}
          onClick={() => setActiveTab("current")}
        >
          📐 Current Project
        </button>
        <button
          className={`pd-tab ${activeTab === "history" ? "pd-tab-active" : ""}`}
          onClick={() => { setActiveTab("history"); onListProjects(); }}
          disabled={isCreating || isBuilding}
          title={isCreating || isBuilding ? "Finish or abort current project first" : ""}
        >
          📁 History
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* CURRENT PROJECT TAB                                          */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {activeTab === "current" && (
        <>
          {/* New Project form — only show when no active project */}
          {!currentProject && !isCreating && (
            <div className="pd-section">
              <h3 className="pd-section-title">➕ New Project</h3>
              <form className="pd-create" onSubmit={handleCreate}>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Condition Based Monitoring board using ROHM ML63Q2557 with vibration sensor, CAN interface, and 24V industrial power input"
                  rows={3}
                />
                <div className="pd-ref-upload">
                  <label className="pd-ref-label">
                    📎 Attach Reference (ZIP with datasheets/schematics)
                    <input
                      type="file"
                      accept=".zip"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) setRefFileName(f.name);
                        if (f && onUploadReference && description.trim()) {
                          const tempId = `proj-${Date.now()}`;
                          onUploadReference(tempId, f, description.trim());
                        }
                      }}
                    />
                  </label>
                  {refFileName && <span className="pd-ref-file">{refFileName}</span>}
                  {referenceStatus === "requesting" && <span className="pd-ref-status">⏳ Requesting upload...</span>}
                  {referenceStatus === "uploading" && <span className="pd-ref-status">📤 Uploading...</span>}
                  {referenceStatus === "processing" && <span className="pd-ref-status">🔍 Gemini analyzing...</span>}
                  {referenceStatus === "done" && <span className="pd-ref-status pd-ref-done">✅ Analyzed</span>}
                  {referenceStatus === "error" && <span className="pd-ref-status pd-ref-error">❌ Failed</span>}
                </div>
                {enrichedPreview && referenceStatus === "done" && (
                  <details className="pd-enriched-preview">
                    <summary>📋 Enriched prompt preview ({enrichedPreview.length} chars)</summary>
                    <pre>{enrichedPreview.slice(0, 2000)}...</pre>
                    <button
                      type="button"
                      className="pd-download-enriched-btn"
                      onClick={() => {
                        const blob = new Blob([enrichedPreview], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'enriched_prompt.txt';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      📥 Download Full Enriched Prompt
                    </button>
                  </details>
                )}
                <label className="pd-gemini-toggle">
                  <input
                    type="checkbox"
                    checked={enrichWithGemini}
                    onChange={(e) => setEnrichWithGemini(e.target.checked)}
                  />
                  ✨ Enrich with Gemini (adds engineering details — may increase complexity)
                </label>
                <button type="submit" disabled={!description.trim() || (!!referenceStatus && referenceStatus !== "done" && referenceStatus !== "error" && referenceStatus !== "")}>
                  🚀 Start
                </button>
              </form>
            </div>
          )}

          {/* Creating spinner */}
          {isCreating && !currentProject && (
            <div className="pd-section pd-creating">
              <h3 className="pd-section-title">⟳ Generating Design Plan...</h3>
              <p className="pd-desc">Claude is analyzing your requirements and generating sheets. This takes 1-2 minutes.</p>
              <button className="pd-abort-btn" onClick={onAbortProject}>
                ✕ Cancel
              </button>
            </div>
          )}

          {/* Active project detail */}
          {currentProject && (
            <div className="pd-section pd-active">
              <div className="pd-project-header">
                <h3 className="pd-section-title">📐 {currentProject.project_name}</h3>
                <div className="pd-project-id">ID: {currentProject.project_id}</div>
                {!isBuilding && (
                  <button className="pd-abort-btn" onClick={onAbortProject} title="Discard and start new project">
                    ✕ Discard
                  </button>
                )}
              </div>
              <p className="pd-desc">{currentProject.description}</p>

              {/* Sheets grid */}
              <div className="pd-sheets-grid">
                {currentProject.sheets.map((s: SheetSummary, i: number) => (
                  <div key={i} className="pd-sheet-card">
                    <span className="pd-sheet-num">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <strong>{s.name}</strong>
                      {s.description && <small>{s.description}</small>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Component status table */}
              {componentStatus.length > 0 && (
                <ComponentStatusTable
                  components={componentStatus}
                  projectId={currentProject.project_id}
                  onFileSelected={onFileSelected}
                  onDownloadComponentJson={onDownloadComponentJson}
                  onGenerateAll={onGenerateAll}
                  onForceUploadToggle={onForceUploadToggle}
                  uploads={datasheetUploads}
                  forcedParts={forcedParts}
                />
              )}

              {/* Symbol Inventory with Approval */}
              {symbolInventory && symbolInventory.components && symbolInventory.components.length > 0 && (
                <div className="pd-section pd-inventory">
                  <h4 className="pd-section-title">🔍 Symbol Inventory</h4>
                  <div className="pd-inventory-summary">
                    <span className="pd-inv-badge pd-inv-ok">✅ {symbolInventory.available_count} available</span>
                    {symbolInventory.generated_count > 0 && (
                      <span className="pd-inv-badge pd-inv-pending">⏳ {symbolInventory.generated_count} pending review</span>
                    )}
                    {symbolInventory.missing_count > 0 && (
                      <span className="pd-inv-badge pd-inv-miss">❌ {symbolInventory.missing_count} missing</span>
                    )}
                  </div>
                  {symbolInventory.pending_review && (
                    <div className="pd-inv-warning">
                      ⚠️ Generated symbols require your approval before schematic building can proceed.
                    </div>
                  )}
                  <table className="pd-inv-table">
                    <thead>
                      <tr>
                        <th>Ref</th>
                        <th>Part</th>
                        <th>Symbol</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {symbolInventory.components.map((c: any, i: number) => (
                        <tr key={i} className={`pd-inv-row pd-inv-${c.status.toLowerCase()}`}>
                          <td>{c.refdes}</td>
                          <td>{c.part_number}</td>
                          <td title={c.reason}>{c.symbol_id || "—"}</td>
                          <td>
                            {c.status === "AVAILABLE_STANDARD" && <span className="pd-inv-status-ok">✅ Standard</span>}
                            {c.status === "APPROVED" && <span className="pd-inv-status-ok">✅ Approved</span>}
                            {c.status === "GENERATED_PENDING_REVIEW" && <span className="pd-inv-status-pending">⏳ Review</span>}
                            {c.status === "MISSING" && <span className="pd-inv-status-miss">❌ Missing</span>}
                            {c.status === "REJECTED" && <span className="pd-inv-status-miss">🚫 Rejected</span>}
                            {c.status === "LOOKUP_FAILED" && <span className="pd-inv-status-warn">⚠️ Lookup failed</span>}
                          </td>
                          <td className="pd-inv-actions">
                            {c.status === "GENERATED_PENDING_REVIEW" && (
                              <>
                                <button
                                  className="pd-inv-btn pd-inv-dl"
                                  onClick={() => onSymbolDownload?.(currentProject.project_id, c.part_number)}
                                  title="Download .kicad_sym for review"
                                >
                                  ⬇ Download
                                </button>
                                <button
                                  className="pd-inv-btn pd-inv-approve"
                                  onClick={() => onSymbolApprove?.(currentProject.project_id, c.part_number, c.artifact_hash)}
                                  title="Approve this symbol for building"
                                >
                                  ✅ Approve
                                </button>
                                <button
                                  className="pd-inv-btn pd-inv-reject"
                                  onClick={() => {
                                    const reason = window.prompt("Reason for rejection (optional):", "");
                                    onSymbolReject?.(currentProject.project_id, c.part_number, reason || "User rejected");
                                  }}
                                  title="Reject this symbol"
                                >
                                  ❌ Reject
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Actions */}
              <div className="pd-actions">
                {/* Stage 1: Build Schematics (generates .kicad_sch + dummy PCB + ZIP) */}
                {!schematicReady && !isBuilding && (
                  <button
                    className="pd-build-btn"
                    onClick={() => onBuildProject(currentProject.project_id)}
                    disabled={isPCBRunning || (symbolInventory && symbolInventory.pending_review)}
                    title={symbolInventory?.pending_review ? "Approve all generated symbols before building" : ""}
                  >
                    {symbolInventory?.pending_review ? "⏳ Approve Symbols First" : "🔨 Build Schematics"}
                  </button>
                )}

                {/* Building spinner */}
                {isBuilding && (
                  <div className="pd-pcb-running">
                    <span>⟳ Building Schematics...</span>
                    <small>Generating .kicad_sch files, custom symbols, and schematic PDF</small>
                  </div>
                )}

                {/* Stage 2: Schematics Built — Download, Review, Upload, then PCB */}
                {schematicReady && !isBuilding && !isPCBRunning && (
                  <div className="pd-pcb-actions">
                    <div className="pd-pcb-status">✅ Schematics Built — Ready for Review</div>

                    {/* Path 1: Continue directly with generated schematics */}
                    <div className="pd-pcb-path">
                      <span className="pd-path-label">Path 1: Use generated schematics as-is</span>
                      <div className="pd-pcb-buttons">
                        <button
                          className="pd-pcb-config-btn"
                          onClick={() => setShowPCBConfig(true)}
                        >
                          ⚙️ Configure PCB
                        </button>
                        <select
                          className="pd-pcb-profile-select"
                          value={pcbProfile}
                          onChange={(e) => setPcbProfile(e.target.value)}
                          disabled={isPCBRunning}
                        >
                          <option value="generic_dynamic">Generic (auto-detect)</option>
                          <option value="cbm_ml63q2557">CBM Edge Node (ML63Q2557)</option>
                          <option value="stm32f4_sensor">STM32F4 Sensor Node</option>
                        </select>
                        <button
                          className="pd-pcb-continue-btn"
                          onClick={() => onBuildPCB(currentProject.project_id, pcbConfig ? { ...pcbConfig, pcb_profile: pcbProfile } : { pcb_profile: pcbProfile } as any)}
                        >
                          ▶️ Continue to PCB
                        </button>
                      </div>
                    </div>

                    {/* Path 2: Download, edit, upload, then PCB */}
                    <div className="pd-pcb-path">
                      <span className="pd-path-label">Path 2: Review &amp; modify in KiCad 9</span>
                      <div className="pd-pcb-buttons">
                        <a
                          href={schematicsArchiveUrl || `https://dndeexgjhs4b7.cloudfront.net/archives/${currentProject.project_id}/${currentProject.project_id}_complete.zip`}
                          className="pd-pcb-download-btn"
                          download
                        >
                          📥 Download Full Package
                        </a>
                        <label className="pd-pcb-upload-btn">
                          📤 Upload Modified (.zip or .kicad_sch)
                          <input
                            type="file"
                            accept=".zip,.kicad_sch"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                handleUploadSchematics(currentProject.project_id, e.target.files);
                              }
                            }}
                          />
                        </label>
                        <button
                          className="pd-pcb-continue-btn"
                          onClick={() => onBuildPCB(currentProject.project_id, pcbConfig ? { ...pcbConfig, pcb_profile: pcbProfile } : { pcb_profile: pcbProfile } as any)}
                          disabled={!uploadedSchematics}
                          title={!uploadedSchematics ? "Upload modified schematics first" : ""}
                        >
                          ▶️ Continue to PCB (with uploads)
                        </button>
                      </div>
                    </div>

                    {uploadedSchematics && (
                      <div className="pd-upload-status">✅ {uploadedSchematics} file(s) uploaded — ready for PCB</div>
                    )}
                  </div>
                )}

                {/* Stage 3: PCB Running */}
                {isPCBRunning && (
                  <div className="pd-pcb-running">
                    <span>⟳ PCB Generation Running...</span>
                    <small>Footprint assignment → Placement → Routing → DRC → Gerbers</small>
                  </div>
                )}

                {/* Stage 4: PCB Complete — Download */}
                {pcbArchiveUrl && !isPCBRunning && (
                  <div className="pd-pcb-complete">
                    <div className="pd-pcb-status">✅ PCB Generation Complete</div>
                    <a href={pcbArchiveUrl} className="pd-pcb-download-btn" download>
                      📦 Download PCB Manufacturing ZIP
                    </a>
                  </div>
                )}

                <div className="pd-download-links">
                  {!pcbArchiveUrl && currentProject.urls.design_document && (
                    <a href={currentProject.urls.design_document} target="_blank" rel="noreferrer" className="pd-dl-link">📄 Design Doc</a>
                  )}
                  {!pcbArchiveUrl && currentProject.urls.block_diagram && (
                    <a href={currentProject.urls.block_diagram} target="_blank" rel="noreferrer" className="pd-dl-link">🔲 Block Diagram</a>
                  )}
                  {currentProject.urls.plan && (
                    <a href={currentProject.urls.plan} target="_blank" rel="noreferrer" className="pd-dl-link">📋 Plan JSON</a>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* HISTORY TAB                                                  */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {activeTab === "history" && (
        <div className="pd-section">
          <div className="pd-hist-toolbar">
            <button className="pd-refresh" onClick={onListProjects}>↻ Refresh</button>
          </div>
          {projects.length === 0 ? (
            <p className="pd-empty">No projects yet.</p>
          ) : (
            <div className="pd-history">
              {[...projects].reverse().map((p) => {
                const isExpanded = expandedId === p.project_id;
                const archiveUrl = (p as any).archive_url || `${ARCHIVE_BASE}/${p.project_id}/${p.project_id}_complete.zip`;
                return (
                  <div key={p.project_id} className="pd-hist-item">
                    <div className="pd-hist-header" onClick={() => setExpandedId(isExpanded ? null : p.project_id)}>
                      <div className="pd-hist-info">
                        <strong>{p.project_name}</strong>
                        <span className="pd-hist-meta">
                          {p.sheet_count} sheets · {new Date(p.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="pd-hist-right">
                        <span className={`pd-status pd-status-${p.status}`}>{p.status}</span>
                        <span className="pd-chevron">{isExpanded ? "▾" : "▸"}</span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="pd-hist-detail">
                        <div className="pd-hist-sheets">
                          {(p.sheet_names || []).map((name: string, i: number) => (
                            <span key={i} className="pd-hist-sheet-tag">{name}</span>
                          ))}
                        </div>
                        <div className="pd-hist-actions">
                          {p.status === "complete" && (
                            <a href={archiveUrl} className="pd-dl-btn pd-dl-zip" download>
                              📦 Schematics ZIP
                            </a>
                          )}
                          {p.status === "complete" && (p as any).schematics_pdf_url && (p as any).schematics_pdf_url !== "" && (
                            <a href={(p as any).schematics_pdf_url} className="pd-dl-btn pd-dl-pdf" download>
                              📄 Schematics PDF
                            </a>
                          )}
                          {p.urls?.design_document && (
                            <a href={(p.urls as any).design_document} target="_blank" rel="noreferrer" className="pd-dl-btn">📄 Design Doc</a>
                          )}
                          {p.urls?.block_diagram && (
                            <a href={(p.urls as any).block_diagram} target="_blank" rel="noreferrer" className="pd-dl-btn">🔲 Block Diagram</a>
                          )}
                          <button className="pd-dl-btn pd-select-btn" onClick={() => { onSelectProject(p.project_id); setActiveTab("current"); }}>
                            📐 Resume
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* PCB Config Modal */}
      <PCBConfigModal
        isOpen={showPCBConfig}
        onClose={() => setShowPCBConfig(false)}
        onSave={(config) => {
          setPcbConfig(config);
          if (currentProject) {
            onConfigurePCB(currentProject.project_id, config);
          }
        }}
        initialConfig={pcbConfig || undefined}
      />
    </div>
  );
};

export default ProjectDashboard;
